import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Spin,
} from "antd";
import { useEffect, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { CloseOutlined } from "@ant-design/icons";

import { useCreateEditTeam } from "../hooks/useCreateEditTeam";
import { getTeamUsers } from "../../../actionCreator/teamManagementActionCreator";
import { searchUsers } from "../../../actionCreator/userManagementActionCreator";
const { TextArea } = Input;
const { Option } = Select;

const CreateTeamForm = ({ initialValues, closeModal }) => {
  const dispatch = useDispatch();
  const [isDisable, setDisable] = useState(false);
  const [users, setUsers] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [form] = Form.useForm();

  const teamUsers = useSelector(
    (state) => state.teamManagementReducer.teamUsers
  );

  const {
    handleCreateUpdateTeam,
    error,
    loading,
    setError,
    handleRemoveUserFromTeam,
  } = useCreateEditTeam();

  useEffect(() => {
    if (initialValues?.id) {
      form.setFieldsValue(initialValues);
      dispatch(getTeamUsers({ id: initialValues?.id }));
    }
  }, [initialValues, form, dispatch]);

  useEffect(() => {
    const fetchDefault = async () => {
      try {
        const res = await dispatch(searchUsers());
        setUsers(res?.content || []);
      } catch (error) {}
    };

    fetchDefault();
  }, [dispatch]);

  const fetchOptions = debounce(async (value) => {
    setFetching(true);
    try {
      const res = await dispatch(searchUsers(value));
      setUsers(res?.content || []);
    } catch (err) {}
    setFetching(false);
  }, 500);

  return (
    <Card
      title={
        <div className="text-theme heading18px">
          {initialValues?.id ? "Edit Team" : "Create Team"}
        </div>
      }
      className="rounded-2xl shadow-md"
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={(val) =>
          handleCreateUpdateTeam(val, initialValues, () => {
            form.resetFields();
            closeModal && closeModal();
          })
        }
        onValuesChange={(changedValues, allValues) => {
          setError();
          if (initialValues?.id) {
            const keysToTrack = [
              "teamName",
              "description",
              "location",
              "managerEmail",
            ];

            const hasChanges = keysToTrack.some(
              (key) =>
                (initialValues[key] || "")?.trim() !==
                (allValues[key] || "")?.trim()
            );
            setDisable(!hasChanges);
          }
        }}
        autoComplete="off"
      >
        {error && <p className="text-error text-center pb-2">{error}</p>}
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Team Name</div>}
              name="teamName"
              rules={[{ required: true, message: "Please enter team name" }]}
            >
              <Input placeholder="Enter team name" />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              label={<div className="fw-medium">Description</div>}
              name="description"
            >
              <TextArea rows={3} placeholder="Enter team description" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={<div className="fw-medium">Location</div>}
              name="location"
            >
              <Input placeholder="Enter location" />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={<div className="fw-medium">Manager</div>}
              name="managerEmail"
            >
              <Select
                showSearch
                placeholder="Search user..."
                onSearch={fetchOptions}
                filterOption={false}
                notFoundContent={fetching ? <Spin size="small" /> : null}
                allowClear
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.email}>
                    {user?.firstName} {user?.lastName} ({user?.email})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {!!initialValues?.id && (
            <Col span={24} className="mb20">
              <div className="fw-medium">Team Users</div>
              {teamUsers?.userTeams?.length ? (
                <>
                  {teamUsers?.userTeams?.map((d) => (
                    <Row className="justify-between mt10 mb10">
                      <Col span={22}>
                        {d?.userName} ({d?.userEmail})
                      </Col>
                      <Col>
                        <Popconfirm
                          title="Remove user"
                          description={`Are you sure to remove ${d?.userName} from team ${initialValues?.teamName}?`}
                          okText="Yes"
                          cancelText="No"
                          onConfirm={() =>
                            handleRemoveUserFromTeam(d, () =>
                              dispatch(getTeamUsers({ id: initialValues?.id }))
                            )
                          }
                        >
                          <CloseOutlined className="text-error" />
                        </Popconfirm>
                      </Col>
                    </Row>
                  ))}
                </>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No users added to this team"
                />
              )}
            </Col>
          )}
        </Row>
        <div className="d-flex align-items-center justify-content-end gap-20">
          {!!initialValues?.id && (
            <Button
              disabled={loading}
              className="secondary-btn"
              onClick={closeModal}
            >
              Cancel
            </Button>
          )}
          <Button
            disabled={loading || isDisable}
            type="primary"
            className="theme-btn"
            htmlType="submit"
            block={!initialValues?.id}
          >
            {initialValues?.id ? "Save Changes" : "Create team"}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default CreateTeamForm;
